import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import type { ComponentProps } from 'react';

type ScaffolderFormProps = ComponentProps<typeof ScaffolderPage>['formProps'];
import { ScaffolderFieldExtensions, createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import {
  AzureSubscriptionPickerField,
  ApplicationDomainsField,
  applicationDomainsValidation,
  SubscriptionViewersField,
  subscriptionViewersValidation,
  Day2WarningsField,
  Day2FrameworkField,
  PlatformIntegrationField,
  CostEstimationField,
  ArkanceReviewStep,
  StepErrorListTemplate,
} from '@internal/plugin-azure-subscription-picker';
import { ModifyApplicationPage } from '@internal/plugin-auth0-onboarding';
import { CompositionsPage } from '@internal/plugin-compositions';

const AzureSubscriptionPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'AzureSubscriptionPicker',
    component: AzureSubscriptionPickerField,
  }),
);

const SubscriptionViewersFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'SubscriptionViewers',
    component: SubscriptionViewersField,
    validation: subscriptionViewersValidation,
  }),
);

const ApplicationDomainsFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ApplicationDomains',
    component: ApplicationDomainsField,
    validation: applicationDomainsValidation,
  }),
);

const PlatformIntegrationFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'PlatformIntegration',
    component: PlatformIntegrationField,
  }),
);

const Day2WarningsFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'Day2Warnings',
    component: Day2WarningsField,
  }),
);

const Day2FrameworkFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'Day2Framework',
    component: Day2FrameworkField,
  }),
);

// Display-only: it reads the whole form, prices it, and never writes anything
// back. Destructive-change acknowledgement is no longer a field — it lives in
// the final Review step, next to the changes it is acknowledging.
const CostEstimationFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'CostEstimation',
    component: CostEstimationField,
  }),
);
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { EntityExcludeTagPicker } from './components/catalog/filters';
import { DefaultFilters } from '@backstage/plugin-catalog-react';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { Navigate } from 'react-router-dom';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { UnifiedThemeProvider } from '@backstage/theme';
import { arkanceLightTheme, arkanceDarkTheme } from './theme';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        provider={{
          id: 'microsoft',
          title: 'Microsoft Entra ID',
          message: 'Sign in with your ARKANCE account',
          apiRef: microsoftAuthApiRef,
        }}
      />
    ),
  },
  themes: [
    {
      id: 'arkance-light',
      title: 'ARKANCE Light',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={arkanceLightTheme} children={children} />
      ),
    },
    {
      id: 'arkance-dark',
      title: 'ARKANCE Dark',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={arkanceDarkTheme} children={children} />
      ),
    },
  ],
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="/catalog" replace />} />
    <Route
      path="/catalog"
      element={
        <CatalogIndexPage
          filters={
            <>
              <DefaultFilters
                initialKind="component"
                initiallySelectedFilter="all"
              />
              <EntityExcludeTagPicker />
            </>
          }
        />
      }
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        // The Day-2 template is never opened blank from the catalog — the
        // "Modify Application" page launches it with a prefilled form. Listing it
        // here would let someone start it with no app selected, which produces a
        // broken run. Hiding it from the list does not block that direct link.
        <ScaffolderPage
          templateFilter={entity => entity.metadata.name !== 'starter-app-day2'}
          // Day-2 gets a changed-fields-only review with a destructive-change
          // gate; every other template keeps the standard schema-driven one.
          components={{ ReviewStepComponent: ArkanceReviewStep }}
          // The wizard keeps a step's validation errors until the next "Next"
          // and shows them on every step, including the ones "Back" leads to.
          // This list only shows the errors about fields on the current step.
          //
          // `templates` is not in Backstage's FormProps type, but the Stepper
          // spreads formProps over its own props (after its ErrorListTemplate),
          // so it is honoured. `transformErrors`, which IS typed, never sees
          // these errors: they arrive as `extraErrors`, which bypass it.
          formProps={
            {
              templates: { ErrorListTemplate: StepErrorListTemplate },
            } as unknown as ScaffolderFormProps
          }
        />
      }
    >
      <ScaffolderFieldExtensions>
        <AzureSubscriptionPickerFieldExtension />
        <SubscriptionViewersFieldExtension />
        <ApplicationDomainsFieldExtension />
        <PlatformIntegrationFieldExtension />
        <Day2WarningsFieldExtension />
        <Day2FrameworkFieldExtension />
        <CostEstimationFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/modify-app" element={<ModifyApplicationPage />} />
    <Route path="/compositions" element={<CompositionsPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
